/**
 * Hiatus — iter69, simplified iter87.408 (user-requested)
 *
 * Splash page shown when a user hits any data API on a tenant/league
 * whose lifecycle_state is `suspended` or `archived`. The 423
 * interceptor in `/app/frontend/src/lib/api.js` writes the response
 * body to sessionStorage and pushes the user here. A CLOSED league's
 * 423 deliberately does NOT route here (see `closed_rivalry_guard.py`)
 * — that's a lighter, self-reversible state the admin UI handles by
 * greying out write controls instead.
 *
 * Copy varies by state:
 *   - suspended  → "On hiatus" (temporary)
 *   - archived   → "Archived" (permanent)
 *
 * No contact-admin CTA — removed per user request (the "Need to
 * reactivate? / Contact platform admin" block and the "Coming back
 * soon" footer badge were both confusing/incorrect for most hits of
 * this page).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const HIATUS_KEY = "tcr_hiatus_info";

export default function Hiatus() {
    const [info, setInfo] = useState(null);

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(HIATUS_KEY);
            if (raw) setInfo(JSON.parse(raw));
        } catch (e) { /* ignore */ }
    }, []);

    const archived = info?.lifecycle_state === "archived";
    const tenantName = info?.tenant_name || "your Rivalry";

    return (
        <div
            data-testid="hiatus-splash"
            className="min-h-screen flex items-center justify-center px-4 bg-ink"
        >
            <div className="brutal-card p-8 max-w-lg w-full text-center space-y-5">
                <h1
                    data-testid="hiatus-headline"
                    className="font-display font-extrabold text-3xl uppercase text-cyber"
                >
                    {archived ? "Archived" : "On hiatus"}
                </h1>
                <p className="text-zinc-300 text-sm leading-relaxed">
                    {archived ? (
                        <>
                            <b className="text-white">{tenantName}</b> has been archived.
                            Standings and history are preserved, but the Rivalry is no
                            longer accepting new activity.
                        </>
                    ) : (
                        <>
                            <b className="text-white">{tenantName}</b> is currently paused.
                            Your standings, rosters, and history are safe — they'll be
                            here when the Rivalry comes back online.
                        </>
                    )}
                </p>
                <div className="border-t border-[#222] pt-4 flex items-center justify-center">
                    <Link
                        to="/"
                        data-testid="hiatus-home-btn"
                        className="brutal-btn-ghost text-xs py-1.5"
                    >
                        <ArrowLeft className="w-3 h-3" /> Home
                    </Link>
                </div>
            </div>
        </div>
    );
}
